import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Play, Users, Smartphone, MapPin, CreditCard } from 'lucide-react';

const HowItWorksPage = ({ onBackToHome }) => {
  const videos = [
    {
      id: 1,
      title: "How QR Codes Work for Shoppers",
      description: "Learn how to scan QR codes and get instant discounts at local businesses",
      embedId: "CgB-yXtBqzs",
      icon: <Smartphone className="h-6 w-6" />
    },
    {
      id: 2,
      title: "How our Chatbots Work for all packages",
      description: "A conversational app which delivers rich info about your offers",
      embedId: "QNwo3iMALpc",
      icon: <Users className="h-6 w-6" />
    },
    {
      id: 3,
      title: "Why Bricks and Mortar businesses use this app",
      description: "Competing with online shopping can be expensive, this app offer instant redeemable discounts",
      embedId: "HRgyyhQDUNE",
      icon: <MapPin className="h-6 w-6" />
    },
    {
      id: 4,
      title: "Benefit for retailers",
      description: "Learn why offering live discounts can increase footfall",
      embedId: "RTHydsYIqTs",
      icon: <CreditCard className="h-6 w-6" />
    },
    {
      id: 5,
      title: "Security",
      description: "Our chatbot is secure learn more",
      embedId: "4z-p8Q5KiiI",
      icon: <CreditCard className="h-6 w-6" />
    }
  ];

  const YouTubeEmbed = ({ embedId, title }) => (
    <div className="aspect-video">
      <iframe
        width="100%"
        height="100%"
        src={`https://www.youtube.com/embed/${embedId}?rel=0&modestbranding=1`}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="rounded-lg"
      ></iframe>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-charcoal mb-2">
              How It Works
            </h1>
            <p className="text-gray-600 text-lg">
              Everything you need to know about using Shoppers Discount Club
            </p>
          </div>
          <Button 
            onClick={onBackToHome}
            variant="outline"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>

        {/* Introduction Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-charcoal mb-4">
              Welcome to Shoppers Discount Club
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              We connect local businesses with shoppers through real-time QR code discounts. 
              Whether you're a shopper looking for deals or a business wanting to attract more customers, 
              these videos will show you exactly how our platform works.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="text-center">
              <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-charcoal mb-2">Scan QR Codes</h3>
              <p className="text-gray-600 text-sm">Instant access to live discounts</p>
            </div>
            <div className="text-center">
              <div className="bg-orange/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-orange" />
              </div>
              <h3 className="font-semibold text-charcoal mb-2">Join as Business</h3>
              <p className="text-gray-600 text-sm">Simple registration process</p>
            </div>
            <div className="text-center">
              <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-8 w-8 text-primary" />
              </div>
              <h3 className="font-semibold text-charcoal mb-2">Setup Profile</h3>
              <p className="text-gray-600 text-sm">Customize your business listing</p>
            </div>
            <div className="text-center">
              <div className="bg-orange/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CreditCard className="h-8 w-8 text-orange" />
              </div>
              <h3 className="font-semibold text-charcoal mb-2">Choose Plan</h3>
              <p className="text-gray-600 text-sm">Flexible pricing options</p>
            </div>
          </div>
        </div>

        {/* Video Tutorials */}
        <div className="space-y-8">
          <h2 className="text-3xl font-bold text-charcoal text-center mb-8">
            Video Tutorials
          </h2>

          {videos.map((video, index) => (
            <Card key={video.id} className="overflow-hidden shadow-lg">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="aspect-video lg:aspect-auto">
                  <YouTubeEmbed embedId={video.embedId} title={video.title} />
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-primary/10 rounded-full w-12 h-12 flex items-center justify-center">
                      {video.icon}
                    </div>
                    <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-charcoal mb-3">
                    {video.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {video.description}
                  </p>
                  <Button
                    variant="outline"
                    className="self-start"
                    onClick={() => window.open(`https://www.youtube.com/watch?v=${video.embedId}`, '_blank')}
                  >
                    <Play className="h-4 w-4 mr-2" />
                    Watch on YouTube
                  </Button>
                </CardContent>
              </div>
            </Card>
          ))}
        </div>

        {/* Call to Action */}
        <div className="bg-gradient-to-br from-primary to-orange rounded-lg p-8 text-center text-white mt-12">
          <h2 className="text-2xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-lg mb-6 opacity-90">
            Join thousands of businesses already using Shoppers Discount Club to attract more customers
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-primary hover:bg-gray-100"
              onClick={() => window.location.href = '/pricing'}
            >
              View Pricing Plans
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-primary"
              onClick={onBackToHome}
            >
              Explore Directory
            </Button>
          </div>
        </div>

        {/* FAQ Quick Links */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-semibold text-charcoal mb-4">
            Still Have Questions?
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/faq'}
            >
              Visit FAQ
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/contact'}
            >
              Contact Support
            </Button>
            <Button 
              variant="outline"
              onClick={() => window.location.href = '/pricing'}
            >
              Pricing Guide
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorksPage;